




































































import { Component, Vue } from 'vue-property-decorator';

import { treatError } from '@/utils';
import isMobile from '@/utils/mobile-check';
import eventBus from '@/utils/eventBus';

import ReferralService from '@/api/ReferralService';

import {
  EBusinessReferralDiscountType,
  IBusinessReferral,
  TBusinessReferralType,
  EBusinessReferralType
} from '@/models/marketingCenter/referral';

import ModalOptions from '@/components/Modal/ModalOptions';
import { Surface, Picture, Typography } from '@/components/atoms';
import { FilledButton } from '@/components/molecules';
import ChangeTypeReferral from '@/components/organisms/Referral/ChangeTypeReferral.vue';
import SetupReferral from '@/components/organisms/Referral/SetupReferral.vue';
import Page from '@/componentsV2/molecules/Page/Page.vue';
import { BreadcrumbData } from '@/componentsV2/molecules/Breadcrumbs/Breadcrumbs.vue';

import { handleCompleteTask } from '@/utils/start-guide';
import { EStartGuideTaskSlug } from '@/models/start-guide';

@Component({
  components: {
    Surface,
    Picture,
    Typography,
    FilledButton,
    ChangeTypeReferral,
    SetupReferral,
    Page
  }
})
export default class ReferralCreation extends Vue {
  referralService = new ReferralService();
  step = 1;
  type = '';
  coins: string | number = '';
  percentage: string | number = '';
  errors = [];
  loadingCampaign = false;
  loading = false;
  campaign: IBusinessReferral = {};

  get tPrefix() {
    return `referralProgram.${this.isEdit ? 'edit' : 'creation'}.step-${
      this.step
    }`;
  }

  get breadcrumbs() {
    const breadcrumbList: BreadcrumbData[] = [
      {
        label: 'Referral Program',
        path: '/referral-program'
      },
      {
        label: 'Select a referral program type'
      }
    ];

    if (this.step === 2) {
      breadcrumbList[breadcrumbList.length - 1].onClick = () => {
        this.step = 1;
      };
      breadcrumbList.push({
        label: this.$t(`${this.tPrefix}.breadcrumbs.${this.type}`) as string
      });
    }

    return breadcrumbList;
  }

  get disabledNext() {
    let isFieldsInvalid =
      !this.coins ||
      !this.percentage ||
      this.coins <= 0 ||
      this.percentage <= 0;

    if (this.type === EBusinessReferralType.REFERRED)
      isFieldsInvalid = !this.percentage || this.percentage <= 0;

    if (this.type === EBusinessReferralType.REFERRING)
      isFieldsInvalid = !this.coins || this.coins <= 0;

    return (
      (this.step === 2 && isFieldsInvalid) || (this.step === 1 && !this.type)
    );
  }

  get isMobileDevice() {
    return isMobile();
  }

  get isReferring() {
    return this.type === EBusinessReferralType.REFERRING;
  }

  get isReferred() {
    return this.type === EBusinessReferralType.REFERRED;
  }

  get campaignType() {
    if (!Number(this.campaign.referringDiscount))
      return EBusinessReferralType.REFERRED;
    if (!Number(this.campaign.referredDiscount))
      return EBusinessReferralType.REFERRING;

    return EBusinessReferralType.BOTH;
  }

  close() {
    this.$router.push('/referral-program');
  }

  changeTypeReferral(value: TBusinessReferralType) {
    const analyticsLabelMapper: Record<TBusinessReferralType, string> = {
      both: 'Rewarding both',
      referred: 'Rewarding only new customers',
      referring: '' // Coming soon
    };

    if (analyticsLabelMapper.hasOwnProperty(value)) {
      const label = analyticsLabelMapper[value];

      this.$analytics({
        eventCategory: 'Referral Program | Referral program setup',
        eventAction: 'change',
        eventLabel: `Clicked on the "${label}" button in ${
          this.isEdit ? 'edit' : 'creation'
        }`
      });
    }

    this.type = value;
  }

  async getReferralProgram() {
    try {
      this.loadingCampaign = true;
      this.$showLoaderWithLocker();

      this.referralService.disableInterception();
      const { data } = await this.referralService.getReferral();
      this.campaign = data;
      this.type = this.campaignType;
    } catch (e) {
      treatError(e);
    } finally {
      this.loadingCampaign = false;
      this.$hideLoader();
    }
  }

  back() {
    this.step -= 1;
    this.scrollToTop();
  }

  analyticsByStep(stepIndex: number) {
    const mapper: Record<number, () => void> = {
      1: () => {
        this.$analytics({
          eventCategory: 'Referral Program | Continue button',
          eventAction: 'click',
          eventLabel: `Clicked on the "Continue" button in ${
            this.isEdit ? 'edit' : 'creation'
          }`
        });
      },
      2: () => {
        this.$analytics({
          eventCategory: 'Referral Program | Save campaign',
          eventAction: 'click',
          eventLabel: `Clicked to save referral program'
          }`
        });
      }
    };

    if (!mapper.hasOwnProperty(stepIndex)) return;

    mapper[stepIndex]();
  }

  next() {
    this.analyticsByStep(this.step);

    if (this.step === 2) {
      this.finish();
      return;
    }

    this.step += 1;
    this.scrollToTop();
  }

  validate() {
    return this.coins > 0 || this.percentage > 0;
  }

  get businessCurrency() {
    return this.$store.getters['internationalization/getCountrySettings']
      .currencySymbol;
  }

  get isEdit() {
    return document.location.href.includes('/edit-type');
  }

  async finish() {
    const validation = this.validate();

    if (!validation) {
      this.$notification('Please fill the highlighted fields below', 'error');
      return;
    }
    this.errors = [];
    this.loading = true;

    const referral: IBusinessReferral = {
      referringDiscount: this.isReferred ? '0' : this.coins,
      referringDiscountType: EBusinessReferralDiscountType.COINS,
      referredDiscount: this.isReferring ? '0' : this.percentage,
      referredDiscountType: EBusinessReferralDiscountType.PERCENTAGE,
      referringMinimumValue: '0',
      referredMinimumValue: '0',
      isActive: true,
      referredPrefix: 'R',
      referringPrefix: 'R'
    };
    if (this.isEdit) {
      referral.isActive = this.campaign.isActive;
      const response = await this.referralService.updateReferral(
        this.campaign.id || '',
        referral
      );
      if (response.status === 200) {
        this.$analytics({
          eventCategory: 'Referral Program | Edit campaign',
          eventAction: 'click',
          eventLabel: 'Edited the campaign'
        });

        this.$router.push('/referral-program');
        this.$notification(
          String(this.$t(`referralProgram.edit.feedback.changed`))
        );
      }
    } else {
      const response = await this.referralService.createReferral(referral);

      handleCompleteTask(EStartGuideTaskSlug.REFERRAL_PROGRAM);

      const params = this.$route.params;

      if (params.taskSlug) {
        this.$router.push('/home');

        return;
      }

      if (response.status === 201) {
        this.$router.push('/referral-program');
        eventBus.$emit(
          'openSubModal',
          new ModalOptions(
            ``,
            ``,
            'none',
            true,
            ``,
            ``,
            ``,
            false,
            'referralCreationFeedback',
            false,
            true,
            true,
            true,
            true
          )
        );
      }
    }
    this.loading = false;
  }

  scrollToTop() {
    const el = document.querySelector('.page--scroll-all');
    if (el) el.scrollTo({ top: 0 });
  }

  changeCoins(value: string) {
    this.coins = value;
  }

  changePercentage(value: string) {
    this.percentage = value;
  }

  async created() {
    if (this.isEdit) {
      await this.getReferralProgram();
      this.changeCoins(Number(this.campaign.referringDiscount).toString());
      this.changePercentage(Number(this.campaign.referredDiscount).toString());
    }
  }
}
